import { useRef, useCallback } from 'react';

interface BarcodeLabelProps {
  itemName: string;
  barcode: string;
  hsnCode?: string;
  rate?: number;
  unit?: string;
}

function BarcodeBars({ barcode }: { barcode: string }) {
  const bars: { width: number; gap: number }[] = [];
  const chars = barcode.split('');
  for (let i = 0; i < chars.length; i++) {
    const code = chars.charCodeAt(i);
    bars.push({ width: 1 + (code % 3), gap: 1 + (code % 2) });
  }
  return (
    <div className="flex items-end h-10" style={{ gap: '1px' }}>
      {bars.map((bar, i) => (
        <div
          key={i}
          style={{ width: `${bar.width}px`, height: `${20 + ((i % 5) * 3)}px` }}
          className="bg-slate-950"
        />
      ))}
    </div>
  );
}

export default function BarcodeLabel({ itemName, barcode, hsnCode, rate, unit }: BarcodeLabelProps) {
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-lg p-3 w-64 shrink-0 print:border-0 print:shadow-none print:p-2">
      <div className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-wide mb-1">InvenPro</div>
      <div className="text-sm font-semibold text-[#1e293b] mb-2 truncate">{itemName}</div>
      <BarcodeBars barcode={barcode} />
      <div className="text-xs font-mono text-[#64748b] mt-1 tracking-widest">{barcode}</div>
      {(hsnCode || rate) && (
        <div className="text-[10px] text-[#64748b] mt-1">
          {hsnCode && <span>HSN: {hsnCode}</span>}
          {hsnCode && rate && <span> &middot; </span>}
          {rate !== undefined && <span>Rate: ₹{rate.toLocaleString('en-IN')}{unit ? `/${unit}` : ''}</span>}
        </div>
      )}
    </div>
  );
}

export function BarcodeLabelSheet({ labels, onClose }: { labels: BarcodeLabelProps[]; onClose: () => void }) {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useCallback(() => {
    const win = window.open('', '_blank', 'width=800,height=600');
    if (!win) return;
    const html = `<!DOCTYPE html><html><head><title>Barcode Labels</title><style>
      @media print { @page { size: A4; margin: 10mm; } body { font-family: monospace; } }
      .label-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; padding: 10px; }
      .label { border: 1px dashed #ccc; border-radius: 6px; padding: 8px; text-align: center; }
      .label .company { font-size: 9px; font-weight: bold; color: #888; text-transform: uppercase; }
      .label .name { font-size: 13px; font-weight: 600; margin: 2px 0; }
      .label .bars { display: flex; align-items: flex-end; gap: 1px; height: 40px; justify-content: center; }
      .label .bars div { background: #000; }
      .label .code { font-size: 11px; font-family: monospace; letter-spacing: 2px; margin-top: 2px; color: #444; }
      .label .info { font-size: 8px; color: #888; margin-top: 1px; }
    </style></head><body onload="window.print();window.close()">
      <div class="label-grid">${labels.map((l) => {
        const chars = l.barcode.split('');
        const bars = chars.map((c, i) => {
          const code = c.charCodeAt(0);
          return `<div style="width:${1 + (code % 3)}px;height:${20 + ((i % 5) * 3)}px"></div>`;
        }).join('');
        return `<div class="label">
          <div class="company">InvenPro</div>
          <div class="name">${l.itemName}</div>
          <div class="bars">${bars}</div>
          <div class="code">${l.barcode}</div>
          ${(l.hsnCode || l.rate !== undefined) ? `<div class="info">${l.hsnCode ? 'HSN: ' + l.hsnCode : ''}${l.hsnCode && l.rate !== undefined ? ' · ' : ''}${l.rate !== undefined ? 'Rate: ₹' + l.rate.toLocaleString('en-IN') + (l.unit ? '/' + l.unit : '') : ''}</div>` : ''}
        </div>`;
      }).join('')}</div>
    </body></html>`;
    win.document.write(html);
    win.document.close();
  }, [labels]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
          <div>
            <h2 className="text-base font-bold text-[#1e293b]">Print Barcode Labels</h2>
            <p className="text-xs text-[#64748b]">{labels.length} label{labels.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handlePrint} className="flex items-center gap-1.5 h-8 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-printer-line" /> Print All
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-[#64748b] cursor-pointer">
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6" ref={printRef}>
          <div className="grid grid-cols-4 gap-4">
            {labels.map((label, i) => (
              <BarcodeLabel key={i} {...label} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
